'use client';
/**
 * LES PARCOURS EN COURS — le tableau de bord de la RQ (lot 5, 18/10/2026).
 *
 * Une ligne par personne qui a quelque chose en cours : dossier de grille
 * ouvert, intégration, déclaration à confirmer, lecture en retard,
 * habilitation échue. Composé par le MÊME moteur que le parcours individuel
 * (`GET ged/rh/parcours`) : ce tableau ne calcule rien, il range. Cliquer une
 * ligne ouvre le parcours de la personne, où tout se fait.
 */
import React from 'react';
import { Pastille } from '@/components/ui';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';

type Ligne = {
  orgId: string; nomAffiche: string; fonction: string | null; progression: number;
  etape: string; etapeEtat: 'sans_objet' | 'a_faire' | 'en_cours' | 'fait' | 'bloque';
  dossiersOuverts: string[]; retards: number;
  aFaire: { forme: number; tuteur: number; rt: number; rq: number; direction: number };
  declarationsEnAttente: number; referencesNonDiffusees: number;
  tuteurDuJour: string | null; prochaineSeance: string | null;
};

const TON: Record<Ligne['etapeEtat'], 'success' | 'warning' | 'danger' | 'muted' | 'info'> = {
  sans_objet: 'muted', a_faire: 'info', en_cours: 'warning', fait: 'success', bloque: 'danger',
};

export function RhParcoursSuivi() {
  const ouvrirDepuis = useGed((s) => s.ouvrirDepuis);
  const [lignes, setLignes] = React.useState<Ligne[] | null>(null);
  const [erreur, setErreur] = React.useState<string | null>(null);
  const [essai, setEssai] = React.useState(0);

  React.useEffect(() => {
    let vivant = true;
    setLignes(null); setErreur(null);
    void fetch('/api/ged/ged/rh/parcours', { credentials: 'include' })
      .then(async (r) => ({ ok: r.ok, corps: await r.json().catch(() => null) }))
      .then(({ ok, corps }) => {
        if (!vivant) return;
        if (ok && Array.isArray(corps?.lignes)) { setLignes(corps.lignes as Ligne[]); return; }
        setErreur(String(corps?.error?.message || 'Les parcours n’ont pas pu être lus.'));
      })
      .catch(() => { if (vivant) setErreur('Les parcours n’ont pas pu être lus.'); });
    return () => { vivant = false; };
  }, [essai]);

  const chargement = lignes === null && !erreur;
  const ouvrir = (l: Ligne) => ouvrirDepuis('rhhab', { view: 'parcours', cle: 'selectedPersonne', valeur: l.orgId });
  const quiDoit = (l: Ligne) => {
    const parts: string[] = [];
    if (l.aFaire.forme) parts.push(`la personne (${l.aFaire.forme})`);
    if (l.aFaire.tuteur) parts.push(`tuteur (${l.aFaire.tuteur})`);
    if (l.aFaire.rt) parts.push(`RT (${l.aFaire.rt})`);
    if (l.aFaire.rq) parts.push(`RQ (${l.aFaire.rq})`);
    if (l.aFaire.direction) parts.push(`Direction (${l.aFaire.direction})`);
    return parts.join(' · ');
  };

  return (
    <section>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Une ligne par personne dont la formation ou l’habilitation est en cours. Ouvrez la ligne : le parcours de la personne porte tout
        — ses lectures, ses déclarations, les avis, la décision. Ce tableau ne fait que ranger.
      </p>
      <Tableau<Ligne>
        id="parcours-suivi" niveau="standard"
        ajusterLargeur
        lignes={lignes || []} cleLigne={(l) => l.orgId}
        ligneProps={(l) => ligneOuvrable(() => ouvrir(l), `Ouvrir le parcours de ${l.nomAffiche}`)}
        chargement={chargement} erreur={erreur} onReessayer={() => setEssai((n) => n + 1)}
        vide={{ titre: 'Aucun parcours en cours.', indice: 'Personne n’a de dossier de grille ouvert, d’intégration en cours ni d’habilitation à renouveler.' }}
        colonnes={[
          { cle: 'nom', libelle: 'Personne', valeur: (l) => l.nomAffiche, epinglee: true, obligatoire: true,
            rendu: (l) => <><strong>{l.nomAffiche}</strong>{l.fonction ? <span style={{ color: 'var(--text-muted)' }}> · {l.fonction}</span> : null}</> },
          { cle: 'progression', libelle: 'Progression', valeur: (l) => l.progression, type: 'nombre', rendu: (l) => `${l.progression} %` },
          { cle: 'etape', libelle: 'Étape', valeur: (l) => l.etape, type: 'liste',
            rendu: (l) => <Pastille ton={TON[l.etapeEtat]} compacte>{l.etape}</Pastille> },
          { cle: 'dossiers', libelle: 'Dossiers ouverts', valeur: (l) => l.dossiersOuverts.join(', '), rendu: (l) => l.dossiersOuverts.join(', ') || '—' },
          { cle: 'retards', libelle: 'Retards', valeur: (l) => l.retards, type: 'nombre',
            rendu: (l) => (l.retards ? <Pastille ton="danger" compacte>{l.retards}</Pastille> : '—') },
          { cle: 'declarations', libelle: 'Déclarations', valeur: (l) => l.declarationsEnAttente, type: 'nombre',
            rendu: (l) => (l.declarationsEnAttente ? <Pastille ton="warning" compacte>{l.declarationsEnAttente} à confirmer</Pastille> : '—') },
          { cle: 'references', libelle: 'Lectures à diffuser', valeur: (l) => l.referencesNonDiffusees, type: 'nombre',
            rendu: (l) => (l.referencesNonDiffusees ? <Pastille ton="info" compacte>{l.referencesNonDiffusees}</Pastille> : '—') },
          { cle: 'qui', libelle: 'Qui doit agir', valeur: (l) => quiDoit(l), rendu: (l) => quiDoit(l) || 'Rien n’attend' },
          { cle: 'tuteur', libelle: 'Tuteur aujourd’hui', valeur: (l) => l.tuteurDuJour || '', rendu: (l) => l.tuteurDuJour || '—' },
          { cle: 'seance', libelle: 'Prochaine séance', valeur: (l) => l.prochaineSeance || '', type: 'date', rendu: (l) => (l.prochaineSeance ? fmtDate(l.prochaineSeance) : '—') },
        ]}
      />
    </section>
  );
}
